'use client'

import React, { FC, ReactElement, useMemo, useCallback } from 'react'
import BitmapGrid from './bitmap-grid'
import BitmapHeader from './bitmap-header'
import useBitmap, {
	HEADER_EVENT,
	HeaderEvent,
	buildListedBlocksSet,
	buildListingsMap,
	parseBitmapNumber,
	type CanvasState
} from '@/utils/hooks/useBitmap'
import Event from '@/utils/event'
import { GlobeVisualization } from '@/components/globe-visualization'
import useEscrows from '@/utils/hooks/useEscrows'
import useWallet from '@/utils/hooks/useWallet'

// Find the center block of the largest cluster of owned blocks
// Blocks are arranged in a 1000x1000 grid where index = row * 1000 + col
function findLargestClusterCenter(ownedBlocks: Set<number>): number | null {
	if (ownedBlocks.size === 0) return null
	if (ownedBlocks.size === 1) return Array.from(ownedBlocks)[0]

	const blocks = Array.from(ownedBlocks).sort((a, b) => a - b)

	// Simple approach: find the median block (center of all owned blocks)
	// This gives a good approximation without complex clustering
	const medianIndex = Math.floor(blocks.length / 2)
	return blocks[medianIndex]
}

const Bitmap: FC<{ slug?: string }> = (props): ReactElement => {
	const [iconSelected, setIconSelected] = React.useState('market-icon')
	const [focusBlock, setFocusBlock] = React.useState<number | null>(null)
	const [filterMode, setFilterMode] = React.useState<CanvasState>('show-all')

	// Fetch escrow data for listed bitmaps
	const { data: escrows } = useEscrows(props.slug)

	// Get current blockchain height
	const { height: currentHeight } = useBitmap(props.slug)

	// Get wallet inscriptions to find owned bitmaps
	const { data: walletData } = useWallet()

	// Build a Set of listed block numbers from escrow names
	const listedBlocks = useMemo(() => {
		return buildListedBlocksSet(escrows ?? [])
	}, [escrows])

	// Build a Map of block number -> listing info for price display
	const listingsMap = useMemo(() => {
		return buildListingsMap(escrows ?? [])
	}, [escrows])

	// Build a Set of owned block numbers from wallet inscriptions
	const ownedBlocks = useMemo(() => {
		const set = new Set<number>()
		const inscriptions = walletData?.inscriptions || []

		for (const inscription of inscriptions) {
			// Only include bitmap collection items
			if (inscription?.collection?.slug === 'bitmap' && inscription?.meta?.name) {
				const blockNum = parseBitmapNumber(inscription.meta.name)
				if (blockNum !== null) {
					set.add(blockNum)
				}
			}
		}

		return set
	}, [walletData?.inscriptions])

	// Build a Map of block number -> full inscription data (for listing)
	const ownedInscriptionsMap = useMemo(() => {
		const map = new Map<number, any>()
		const inscriptions = walletData?.inscriptions || []

		for (const inscription of inscriptions) {
			// Only include bitmap collection items
			if (inscription?.collection?.slug === 'bitmap' && inscription?.meta?.name) {
				const blockNum = parseBitmapNumber(inscription.meta.name)
				if (blockNum !== null) {
					map.set(blockNum, inscription)
				}
			}
		}

		return map
	}, [walletData?.inscriptions])

	// Handler for focusing on my lands
	const handleFocusMyLands = useCallback(() => {
		const centerBlock = findLargestClusterCenter(ownedBlocks)
		if (centerBlock !== null) {
			setFocusBlock(centerBlock)
		}
	}, [ownedBlocks])

	React.useEffect(() => {
		const handleEvent = (e: HeaderEvent) => {
			if (e?.type) {
				setIconSelected(e.type)
			}
			if (e?.block !== undefined) {
				setFocusBlock(e.block)
			}
			if (e?.tab) {
				setFilterMode(e.tab)
			}
			if (e?.focusMyLands) {
				// Small delay to let filter mode update first
				setTimeout(() => handleFocusMyLands(), 100)
			}
		}

		Event.listen(HEADER_EVENT, handleEvent)

		return () => {
			Event.removeListener(HEADER_EVENT, handleEvent)
		}
	}, [handleFocusMyLands])

	const viewMode = iconSelected === 'market-icon' ? 'grid' : 'globe'
	const showGlobe = iconSelected === 'globe-icon' || iconSelected === 'market-icon'

	return (
		<div className="fixed inset-x-0 bottom-8 top-[var(--ow-header-h)] z-30 flex flex-col overflow-hidden bg-background">
			<BitmapHeader slug={props.slug as any} />
			{/* The map occupies the remaining space below the toolbar. */}
			<div className="relative min-h-0 flex-1">
				{showGlobe && (
					<React.Suspense
						fallback={
							<div className="flex justify-center items-center h-full">Loading 3D Globe…</div>
						}
					>
						<GlobeVisualization
							initialViewMode={viewMode}
							listedBlocks={listedBlocks}
							ownedBlocks={ownedBlocks}
							ownedInscriptionsMap={ownedInscriptionsMap}
							listingsMap={listingsMap}
							focusBlock={focusBlock}
							onFocusComplete={() => setFocusBlock(null)}
							currentHeight={currentHeight}
							filterMode={filterMode}
						/>
					</React.Suspense>
				)}
				{!showGlobe && <BitmapGrid slug={props.slug as any} />}
			</div>

		</div>
	)
}

export default Bitmap
